import { useNavigate } from "react-router-dom";
import { useLang } from "../../contexts/LangContext";
import { useLogSession } from "../../contexts/LogSessionContext";
import { useUserData } from "../../contexts/UserContext";
import { v4 as uuidv4 } from "uuid";
import { useEffect, useState } from "react";
import fishSwimmingGif from '../../assets/site-images/fish-swimming.gif';

export default function Submitted() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { 
    mode, 
    person, 
    setcurrentMealRecord, 
    currentDayRecord, 
    setcurrentDayRecord, 
    setNewFishItem,
    exitFallback
  } = useLogSession();
  const { updatePerson } = useUserData();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!currentDayRecord) {
      navigate(exitFallback || mode === "survey" ? "/survey/person-list" : "/user", { replace: true });
    }
  }, []);

  const handleYes = () => {
    // Reset only the meal record and go to which-meal for the same day
    setcurrentMealRecord({
      id: uuidv4(),
      meal_type: "lunch",
      fish_items: [],
      updated_at: new Date().toISOString(),
    });
    navigate("../which-meal");
  };

  const handleNo = async () => {
    // Data already saved by ReviewLog's commitMealToDay
    // Just add a small delay for safety and clear session
    console.log('[Submitted] Finishing session (data already saved)...');
    
    // Give extra time for any pending storage operations
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Clear session and navigate
    setcurrentDayRecord(null);
    navigate(
      exitFallback || (mode === "survey" ? "/survey/person-list" : "/user"),
      { replace: true }
    );
  };

  const handleAddAnotherDay = async () => {
    // Data already saved by ReviewLog's commitMealToDay
    // Just clear session and navigate
    console.log('[Submitted] Adding another day (data already saved)...');
    
    // Give extra time for any pending storage operations
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Reset session state and navigate
    setcurrentDayRecord(null);
    setcurrentMealRecord(null);
    setNewFishItem(null);

    navigate(`/log-history/${person.id}`);
  };

  return (
    <div className="flex flex-col items-center justify-between w-full grow text-center">
      {isSaving && (
        <div className="fixed inset-0 bg-white bg-opacity-50 backdrop-blur flex items-center justify-center z-50">
          <div className="p-8 flex flex-col items-center gap-4">
            <img src={fishSwimmingGif} alt="Loading..." className="w-40 h-auto" />
            <p className="text-xl font-semibold text-gray-700">{t("reviewLog_Saving")}</p>
          </div>
        </div>
      )}
      <div className="flex flex-col items-center gap-10 mt-12 px-scr">
        <h1 className="text-3xl font-bold text-green-700">
          {t("submitted_Heading")}
        </h1>
        <p className="text-gray-600">{t("submitted_SubText")}</p>
        <div className="flex flex-col gap-4 w-full max-w-sm">
          <button
            type="button"
            onClick={handleYes}
            disabled={isSaving}
            className="w-full button-base button-bg-grad-green flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            <span>{t("submitted_AddAnotherMeal")}</span>
          </button>

          <button
            type="button"
            onClick={handleNo}
            disabled={isSaving}
            className="w-full button-base button-bg-grad-2 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.25c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75a.75.75 0 0 1 .75-.75 2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282m0 0h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23H5.904m10.598-9.75H14.25M5.904 18.5c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 0 1-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 9.953 4.167 9.5 5 9.5h1.053c.472 0 .745.556.5.96a8.958 8.958 0 0 0-1.302 4.665c0 1.194.232 2.333.654 3.375Z" />
            </svg>
            <span>{t("submitted_Finish")}</span>
          </button>
        </div>     
      </div>

       {/* Add Another Day Button - Separated at bottom */}
      <div className="bottom-nav-bar flex justify-center items-center w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
        <button
          type="button"
          onClick={handleAddAnotherDay}
          disabled={isSaving}
          className="max-w-sm button-small button-bg-grad-purple flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          <span>{t("submitted_AddAnotherDay")}</span>
        </button>
      </div>
    </div>
  );
}
